import type { ReactNode } from 'react';
import { AlertTriangle, CreditCard, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from 'helpers/cn';
import { useActivationBanner } from '@/state/billing/useActivationBanner';
import { FOCUS_RING } from './Dashboard/ConsolePanel';

/**
 * The one billing surface rendered outside Settings, mounted in DashboardLayout
 * alongside (not replacing) GlobalAnnouncement, a separate, unrelated system.
 * What it says is derived entirely in useActivationBanner; this only draws it.
 */
export const ActivationBanner = () => {
  const { banner } = useActivationBanner();
  if (!banner) return null;

  const { tone, message, action, onDismiss } = banner;
  const icon =
    tone === 'destructive' ? (
      <AlertTriangle className="size-4" strokeWidth={1.5} />
    ) : (
      <CreditCard className="size-4" strokeWidth={1.5} />
    );

  return (
    <Banner tone={tone} icon={icon} onDismiss={onDismiss}>
      <span className="min-w-0 flex-1">{message}</span>
      {!!action && (
        <Button
          size="sm"
          variant={tone === 'destructive' ? 'outline' : 'default'}
          className="h-8 shrink-0 rounded-lg text-body"
          onClick={action.onClick}
        >
          {action.label}
        </Button>
      )}
    </Banner>
  );
};

type BannerProps = {
  tone: 'neutral' | 'destructive';
  icon: ReactNode;
  onDismiss?: () => void;
  children: ReactNode;
};

/** The console's notice shell (see GlobalAlerts' AlertBanner): a flat panel with a toned hairline. */
const Banner = ({ tone, icon, onDismiss, children }: BannerProps) => (
  <div
    role={tone === 'destructive' ? 'alert' : 'status'}
    className={cn(
      'bg-panel rounded-panel flex flex-wrap items-center gap-3 border px-3.5 py-2.5',
      tone === 'destructive' ? 'border-danger-line' : 'border-line',
    )}
  >
    <span
      className={cn(
        'flex size-8 shrink-0 items-center justify-center rounded-lg border',
        tone === 'destructive' ? 'border-danger-line text-danger' : 'border-line text-dim',
      )}
    >
      {icon}
    </span>
    <div className="text-foreground flex min-w-0 flex-1 flex-wrap items-center gap-3 text-body">{children}</div>
    {!!onDismiss && (
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className={cn(
          'text-dim hover:bg-hover hover:text-foreground flex size-6 shrink-0 items-center justify-center rounded-lg',
          'transition-colors duration-150',
          FOCUS_RING,
        )}
      >
        <X className="size-4" strokeWidth={1.5} />
      </button>
    )}
  </div>
);
